import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  LogOut,
  Share2,
  FileText,
  Trash2,
  Copy,
  LayoutGrid,
  Crown,
  Shield,
  Lock,
  X,
  Sparkles,
  ArrowRight,
  BookmarkPlus,
  Bookmark,
  CheckCircle,
  Building2,
  Code2,
  Workflow,
  Factory,
  Target,
  Megaphone,
  Film,
  Grid3X3,
  Layers,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { diagramTemplates, templateCategories } from "@/components/diagram/templates";
import DiagramThumbnail from "@/components/diagram/DiagramThumbnail";
import { TemplateMiniPreview } from "@/components/diagram/TemplateMiniPreview";
import { NotificationBell } from "@/components/NotificationBell";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

const typeLabels = {
  orgchart: "Organigramme",
  scenario: "Scénario",
  process: "Processus",
};

const typeColors = {
  orgchart: "#6366F1",
  scenario: "#F43F5E",
  process: "#F59E0B",
};

const catIcons = {
  grid: Grid3X3,
  building: Building2,
  code: Code2,
  workflow: Workflow,
  factory: Factory,
  target: Target,
  megaphone: Megaphone,
  film: Film,
};

const catDotColors = {
  all: "bg-indigo-500",
  entreprise: "bg-violet-500",
  tech: "bg-blue-500",
  workflow: "bg-amber-500",
  process: "bg-emerald-500",
  strategie: "bg-rose-500",
  marketing: "bg-orange-500",
  media: "bg-pink-500",
  general: "bg-gray-400",
  custom: "bg-gray-400",
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [diagrams, setDiagrams] = useState([]);
  const [sharedDiagrams, setSharedDiagrams] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newTemplate, setNewTemplate] = useState("empty");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("mine");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userTemplates, setUserTemplates] = useState([]);
  const [tplCat, setTplCat] = useState("all");
  const [tplSearch, setTplSearch] = useState("");
  const [pendingActions, setPendingActions] = useState([]);
  const [trashDiagrams, setTrashDiagrams] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteConfirmTitle, setDeleteConfirmTitle] = useState("");

  useEffect(() => {
    if (user && user.onboarding_done === false) {
      // First-time user: redirect to first diagram with tour
      const goToTour = async () => {
        try {
          const { data } = await axios.get(`${API}/diagrams`, { withCredentials: true });
          if (data.length > 0) {
            navigate(`/editor/${data[0].id}?tour=1`);
          }
        } catch {
          setShowWelcome(true);
        }
      };
      goToTour();
    }
  }, [user, navigate]);

  const loadDiagrams = async () => {
    try {
      const [mine, shared, tpls, actions, trash] = await Promise.all([
        axios.get(`${API}/diagrams`, { withCredentials: true }),
        axios.get(`${API}/diagrams/shared`, { withCredentials: true }),
        axios.get(`${API}/templates`, { withCredentials: true }),
        axios.get(`${API}/diagrams/pending-actions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/diagrams/trash`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setDiagrams(mine.data);
      setSharedDiagrams(shared.data);
      setUserTemplates(tpls.data);
      setPendingActions(actions.data);
      setTrashDiagrams(trash.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadDiagrams();
  }, []);

  const createDiagram = async () => {
    try {
      // Auto-detect type from template category
      const selectedTpl = diagramTemplates[newTemplate];
      let autoType = "orgchart";
      if (selectedTpl) {
        const cat = selectedTpl.category || "";
        if (cat === "stratégie") autoType = "scenario";
        else if (cat === "workflow" || cat === "process") autoType = "process";
      }
      const { data } = await axios.post(
        `${API}/diagrams`,
        { title: newTitle || "Nouveau diagramme", diagram_type: autoType },
        { withCredentials: true }
      );
      // Apply template if selected
      const tpl = diagramTemplates[newTemplate];
      if (tpl && tpl.nodes.length > 0) {
        const updateBody = { nodes: tpl.nodes, edges: tpl.edges };
        // Include pages if template has them (multi-page templates)
        if (tpl.pages && tpl.pages.length > 0) {
          updateBody.pages = tpl.pages;
        }
        await axios.put(
          `${API}/diagrams/${data.id}`,
          updateBody,
          { withCredentials: true }
        );
      }
      setDialogOpen(false);
      setNewTitle("");
      setNewTemplate("empty");
      setTplCat("all");
      setTplSearch("");
      navigate(`/editor/${data.id}`);
    } catch (e) {
      if (e.response?.status === 403) {
        setDialogOpen(false);
        setUpgradeOpen(true);
      } else {
        console.error(e);
      }
    }
  };

  const handleUpgrade = async (plan = "pro") => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: origin, plan },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const dismissWelcome = async () => {
    setShowWelcome(false);
    try {
      await axios.post(`${API}/auth/onboarding-done`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
  };

  const deleteDiagram = async (id, e) => {
    e.stopPropagation();
    const d = diagrams.find(x => x.id === id);
    setDeleteConfirmId(id);
    setDeleteConfirmTitle(d?.title || "Sans titre");
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await axios.delete(`${API}/diagrams/${deleteConfirmId}`, { withCredentials: true });
      toast.success("Deplace dans la corbeille");
      loadDiagrams();
    } catch (e) {
      toast.error("Erreur");
    }
    setDeleteConfirmId(null);
    setDeleteConfirmTitle("");
  };

  const restoreDiagram = async (id) => {
    try {
      await axios.post(`${API}/diagrams/${id}/restore`, {}, { withCredentials: true });
      toast.success("Diagramme restaure !");
      loadDiagrams();
    } catch {
      toast.error("Erreur");
    }
  };

  const permanentDelete = async (id) => {
    try {
      await axios.delete(`${API}/diagrams/${id}/permanent`, { withCredentials: true });
      toast.success("Supprime definitivement");
      loadDiagrams();
    } catch {
      toast.error("Erreur");
    }
  };

  const duplicateDiagram = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/diagrams/${id}/duplicate`, {}, { withCredentials: true });
      toast.success(`Diagramme duplique : ${data.title}`);
      loadDiagrams();
    } catch (e) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const currentList = activeTab === "mine" ? diagrams : activeTab === "shared" ? sharedDiagrams : activeTab === "trash" ? trashDiagrams : [];

  return (
    <div className="min-h-screen bg-gray-50/50" data-testid="dashboard-page">
      {/* Header */}
      <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50">
        <h1 className="font-heading text-lg sm:text-xl font-bold text-gray-900 shrink-0">
          CardFlow
        </h1>
        <div className="flex items-center gap-1.5 sm:gap-3">
          {user?.plan === "enterprise" && (
            <span className="hidden sm:flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200" data-testid="premium-badge">
              <Crown size={11} /> Entreprise
            </span>
          )}
          {user?.plan === "pro" && (
            <span className="hidden sm:flex items-center gap-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200" data-testid="pro-badge">
              <Crown size={11} /> Pro
            </span>
          )}
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
              data-testid="admin-link"
            >
              <Shield size={14} /> <span className="hidden sm:inline">Admin</span>
            </button>
          )}
          <NotificationBell />
          <button
            onClick={() => navigate("/profile")}
            className="hidden sm:flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="profile-link"
          >
            {user?.name}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-700 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:p-8">
        {/* Welcome onboarding banner */}
        {showWelcome && (
          <div className="mb-8 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="welcome-banner">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            <button
              onClick={dismissWelcome}
              className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="dismiss-welcome"
            >
              <X size={14} />
            </button>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={20} />
                <h2 className="text-lg font-bold">Bienvenue sur CardFlow, {user?.name} !</h2>
              </div>
              <p className="text-sm text-indigo-100 mb-4 max-w-lg">
                Nous avons créé un diagramme exemple pour vous aider a demarrer. Explorez-le pour decouvrir toutes les possibilites : glisser-deposer, connexions, propriétés editables, et bien plus.
              </p>
              <div className="flex flex-wrap gap-3">
                {diagrams.length > 0 && (
                  <button
                    onClick={() => {
                      dismissWelcome();
                      navigate(`/editor/${diagrams[diagrams.length - 1].id}`);
                    }}
                    className="bg-white text-indigo-700 px-4 py-2 rounded-lg text-xs font-bold hover:bg-indigo-50 transition-colors flex items-center gap-1.5 shadow-sm"
                    data-testid="open-demo-btn"
                  >
                    Ouvrir le diagramme exemple <ArrowRight size={14} />
                  </button>
                )}
                <button
                  onClick={dismissWelcome}
                  className="bg-white/10 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-white/20 transition-colors"
                  data-testid="skip-onboarding-btn"
                >
                  J'ai compris, merci
                </button>
              </div>
              <div className="flex flex-wrap gap-4 mt-5 text-[11px] text-indigo-200">
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center"><Plus size={10} /></div>
                  Ajoutez des cartes
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center"><Share2 size={10} /></div>
                  Connectéz-les entre elles
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center"><FileText size={10} /></div>
                  Générez un cahier des charges
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Pending Actions Banner */}
        {pendingActions.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="pending-actions">
            <h3 className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
              <Crown size={12} className="text-amber-500" /> Actions en attente ({pendingActions.length})
            </h3>
            <div className="space-y-2">
              {pendingActions.slice(0, 5).map((a, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/editor/${a.diagram_id}`)}
                  className="w-full flex items-center gap-3 bg-white rounded-lg p-2.5 border border-amber-100 hover:border-amber-300 text-left transition-colors"
                  data-testid={`pending-action-${i}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${a.type === "review" ? (a.last_status === "approved" ? "bg-emerald-100" : "bg-amber-100") : "bg-blue-100"}`}>
                    {a.type === "review" ? (
                      a.last_status === "approved" ? <CheckCircle size={14} className="text-emerald-600" /> : <Crown size={14} className="text-amber-600" />
                    ) : (
                      <Share2 size={14} className="text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{a.diagram_title}</p>
                    <p className="text-[10px] text-gray-400 truncate">
                      {a.type === "review" ? `${a.last_author} — ${a.last_status === "approved" ? "Approuvé" : "À modifier"} (${a.comment_count} commentaire${a.comment_count > 1 ? "s" : ""})` : `Partage par ${a.owner_name}`}
                    </p>
                  </div>
                  <ArrowRight size={12} className="text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* License Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="license-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.plan === "enterprise" ? "bg-amber-100" : user?.plan === "pro" ? "bg-blue-100" : "bg-gray-100"}`}>
              <Crown size={18} className={user?.plan === "enterprise" ? "text-amber-600" : user?.plan === "pro" ? "text-blue-600" : "text-gray-400"} />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-900">
                Plan {user?.plan === "enterprise" ? "Entreprise" : user?.plan === "pro" ? "Pro" : "Gratuit"}
                {user?.company && <span className="text-gray-400 font-normal ml-2">— {user.company}</span>}
              </p>
              <p className="text-[10px] text-gray-400">
                {user?.plan === "enterprise" ? "Diagrammes illimités, collab, IA, export" : user?.plan === "pro" ? "20 diagrammes, fonctionnalités avancees" : `${diagrams.length} diagramme${diagrams.length !== 1 ? "s" : ""} utilise${diagrams.length !== 1 ? "s" : ""}`}
              </p>
            </div>
          </div>
          {(!user?.plan || user?.plan === "free") && (
            <button
              onClick={() => window.open("/#pricing", "_blank")}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-colors whitespace-nowrap"
              data-testid="upgrade-plan-btn"
            >
              Passer Pro ou Entreprise
            </button>
          )}
          {user?.plan === "pro" && (
            <button
              onClick={() => window.open("/#pricing", "_blank")}
              className="bg-amber-500 text-white px-4 py-2 rounded-lg text-[10px] font-bold hover:bg-amber-600 transition-colors whitespace-nowrap"
              data-testid="upgrade-enterprise-btn"
            >
              Passer Entreprise
            </button>
          )}
        </div>

        {/* Title + Create */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Mes Diagrammes</h2>
            {!user?.premium && diagrams.length >= 1 && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Lock size={10} /> {diagrams.length}/1 diagramme gratuit utilise
              </p>
            )}
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 hover:bg-indigo-700 transition-colors shadow-sm"
                data-testid="create-diagram-btn"
              >
                <Plus size={16} /> Nouveau
              </button>
            </DialogTrigger>
            <DialogContent className="!rounded-none sm:!rounded-2xl border-0 shadow-2xl bg-white sm:!max-w-2xl !p-0 !gap-0 max-h-[100dvh] sm:max-h-[85vh] overflow-hidden">
              <div className="flex flex-col h-full max-h-[85vh]">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                  <DialogHeader>
                    <DialogTitle className="font-bold text-lg tracking-tight">
                      Nouveau diagramme
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-400">
                      Nommez votre projet et choisissez un template.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4">
                    <Input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="Mon diagramme"
                      className="rounded-xl border-gray-200 h-11 text-sm font-medium placeholder:text-gray-300"
                      data-testid="new-diagram-title-input"
                    />
                  </div>
                </div>

                {/* Category tabs + search */}
                <div className="px-6 pt-4 pb-2 space-y-3">
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                    {templateCategories.map((cat) => {
                      const Icon = catIcons[cat.icon] || Grid3X3;
                      const isActive = tplCat === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setTplCat(cat.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all border ${
                            isActive
                              ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                              : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700"
                          }`}
                          data-testid={`tpl-cat-${cat.id}`}
                        >
                          <Icon size={12} />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input
                      value={tplSearch}
                      onChange={(e) => setTplSearch(e.target.value)}
                      placeholder="Rechercher un template..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 bg-gray-50/50 placeholder:text-gray-300"
                      data-testid="tpl-search-input"
                    />
                  </div>
                </div>

                {/* Templates grid */}
                <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ maxHeight: "400px" }}>
                  {/* Vierge — always first */}
                  {(tplCat === "all" || tplCat === "general") && !tplSearch && (
                    <div className="mb-4">
                      <button
                        onClick={() => setNewTemplate("empty")}
                        className={`w-full p-4 rounded-xl border-2 border-dashed text-left transition-all flex items-center gap-4 ${
                          newTemplate === "empty"
                            ? "border-indigo-500 bg-indigo-50/50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                        }`}
                        data-testid="template-empty"
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          newTemplate === "empty" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-400"
                        }`}>
                          <Plus size={18} />
                        </div>
                        <div>
                          <span className="font-semibold text-sm text-gray-900 block">Canvas vierge</span>
                          <span className="text-[11px] text-gray-400">Commencez de zero, glissez-deposez vos éléments</span>
                        </div>
                        {newTemplate === "empty" && (
                          <div className="ml-auto"><CheckCircle size={18} className="text-indigo-600" /></div>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Grouped templates by category */}
                  {(() => {
                    const allTpls = Object.entries(diagramTemplates).filter(([key]) => key !== "empty");
                    const searchLower = tplSearch.toLowerCase();

                    // Filter by search
                    const searched = tplSearch
                      ? allTpls.filter(([, tpl]) => tpl.label.toLowerCase().includes(searchLower) || (tpl.description || "").toLowerCase().includes(searchLower))
                      : allTpls;

                    // Group by category
                    const groups = {};
                    searched.forEach(([key, tpl]) => {
                      const cat = tpl.category || "general";
                      if (tplCat !== "all" && cat !== tplCat) return;
                      if (!groups[cat]) groups[cat] = [];
                      groups[cat].push([key, tpl]);
                    });

                    const catLabels = {};
                    templateCategories.forEach(c => { catLabels[c.id] = c.label; });

                    if (Object.keys(groups).length === 0) {
                      return (
                        <div className="text-center py-12 text-gray-400">
                          <Layers size={32} className="mx-auto mb-2 opacity-40" />
                          <p className="text-sm">Aucun template trouve</p>
                        </div>
                      );
                    }

                    return Object.entries(groups).map(([catId, templates]) => (
                      <div key={catId} className="mb-5">
                        {tplCat === "all" && (
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${catDotColors[catId] || "bg-gray-400"}`} />
                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                              {catLabels[catId] || catId}
                            </h4>
                            <span className="text-[10px] text-gray-300">{templates.length}</span>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {templates.map(([key, tpl]) => {
                            const isSelected = newTemplate === key;
                            const nodeCount = tpl.nodes?.length || 0;
                            const hasPages = tpl.pages && tpl.pages.length > 0;
                            return (
                              <button
                                key={key}
                                onClick={() => setNewTemplate(key)}
                                className={`group relative rounded-xl border text-left transition-all overflow-hidden ${
                                  isSelected
                                    ? "border-indigo-500 bg-indigo-50/60 ring-2 ring-indigo-500/15 shadow-sm"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                                }`}
                                data-testid={`template-${key}`}
                              >
                                {/* Mini preview */}
                                <div className="border-b border-gray-100 flex items-center justify-center py-2 px-2 bg-white/50">
                                  <TemplateMiniPreview nodes={tpl.nodes} edges={tpl.edges} width={140} height={70} />
                                </div>
                                <div className="p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-semibold text-xs text-gray-900 block truncate">{tpl.label}</span>
                                      <span className="text-[10px] text-gray-400 block mt-0.5 line-clamp-1 leading-relaxed">
                                        {tpl.description || "Template predefini"}
                                      </span>
                                    </div>
                                    {isSelected && <CheckCircle size={14} className="text-indigo-600 shrink-0 mt-0.5" />}
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-[9px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                      {nodeCount} éléments
                                    </span>
                                    {hasPages && (
                                      <span className="text-[9px] font-medium text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">
                                        Multi-pages
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Footer with selected template + create button */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3">
                  <div className="text-xs text-gray-400 flex items-center gap-2 min-w-0">
                    <Layers size={13} className="shrink-0 text-gray-300" />
                    <span className="truncate">
                      {newTemplate === "empty" ? "Canvas vierge" : diagramTemplates[newTemplate]?.label || "—"}
                    </span>
                  </div>
                  <button
                    onClick={createDiagram}
                    className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2 shrink-0"
                    data-testid="confirm-create-diagram-btn"
                  >
                    <Plus size={15} />
                    Créer
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveTab("mine")}
            className={`px-3 sm:px-4 py-2 rounded-md text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
              activeTab === "mine"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="my-diagrams-tab"
          >
            <LayoutGrid size={13} className="inline mr-1 -mt-0.5" />
            Schemas ({diagrams.length})
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-3 sm:px-4 py-2 rounded-md text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
              activeTab === "templates"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="my-templates-tab"
          >
            <Bookmark size={13} className="inline mr-1 -mt-0.5" />
            Modèles ({userTemplates.length})
          </button>
          <button
            onClick={() => setActiveTab("shared")}
            className={`px-3 sm:px-4 py-2 rounded-md text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
              activeTab === "shared"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="shared-diagrams-tab"
          >
            <Share2 size={13} className="inline mr-1 -mt-0.5" />
            Partages ({sharedDiagrams.length})
          </button>
          {trashDiagrams.length > 0 && (
            <button
              onClick={() => setActiveTab("trash")}
              className={`px-3 sm:px-4 py-2 rounded-md text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
                activeTab === "trash"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="trash-tab"
            >
              <Trash2 size={13} className="inline mr-1 -mt-0.5" />
              Corbeille ({trashDiagrams.length})
            </button>
          )}
        </div>

        {/* Diagram Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentList.map((d) => (
            <div
              key={d.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden"
              onClick={() => activeTab !== "trash" && navigate(`/editor/${d.id}`)}
              data-testid={`diagram-card-${d.id}`}
            >
              {/* Thumbnail preview */}
              <div className="border-b border-gray-100 overflow-hidden">
                <DiagramThumbnail miniNodes={d.mini_nodes} miniEdges={d.mini_edges} width={300} height={90} />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm text-gray-900 mb-1.5 truncate">{d.title}</h3>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: (typeColors[d.diagram_type] || "#6366F1") + "15",
                      color: typeColors[d.diagram_type] || "#6366F1",
                    }}
                  >
                    {typeLabels[d.diagram_type] || d.diagram_type}
                  </span>
                  <span className="text-[10px] text-gray-400">{d.node_count} elem.</span>
                  {d.page_count > 0 && <span className="text-[10px] text-violet-500">{d.page_count} pages</span>}
                </div>
                {activeTab === "shared" && d.owner && (
                  <p className="text-[10px] text-gray-400 mt-1">Par {d.owner.name}</p>
                )}
              </div>
              <div className="border-t border-gray-100 px-4 py-2 flex justify-between items-center">
                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                  {d.shared_with?.length > 0 && <Share2 size={10} />}
                  {d.updated_at ? new Date(d.updated_at).toLocaleDateString("fr-FR") : ""}
                </span>
                {activeTab === "mine" && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={(e) => duplicateDiagram(d.id, e)}
                      className="text-gray-300 hover:text-indigo-500"
                      title="Dupliquer"
                      data-testid={`duplicate-diagram-${d.id}`}
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={(e) => deleteDiagram(d.id, e)}
                      className="text-gray-300 hover:text-rose-500"
                      title="Supprimer"
                      data-testid={`delete-diagram-${d.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
                {activeTab === "trash" && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); restoreDiagram(d.id); }}
                      className="text-emerald-500 hover:text-emerald-700 text-[10px] font-semibold flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg"
                      data-testid={`restore-diagram-${d.id}`}
                    >
                      Restaurer
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); permanentDelete(d.id); }}
                      className="text-rose-400 hover:text-rose-600 text-[10px] font-semibold"
                      title="Supprimer definitivement"
                      data-testid={`perm-delete-${d.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {currentList.length === 0 && (
            <div className="col-span-full text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
              {activeTab === "mine" ? (
                <>
                  <FileText
                    size={40}
                    className="mx-auto mb-3 text-gray-300"
                  />
                  <p className="text-gray-400 text-sm">
                    Aucun diagramme. Créez-en un !
                  </p>
                </>
              ) : (
                <>
                  <Share2
                    size={40}
                    className="mx-auto mb-3 text-gray-300"
                  />
                  <p className="text-gray-400 text-sm">
                    Aucun diagramme partage avec vous
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* User Templates Grid */}
        {activeTab === "templates" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {userTemplates.map((t) => (
              <div
                key={t.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden group"
                data-testid={`template-card-${t.id}`}
              >
                <div className="border-b border-gray-100 overflow-hidden">
                  <DiagramThumbnail miniNodes={t.mini_nodes} width={300} height={90} />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-sm text-gray-900 mb-1 truncate">{t.title}</h3>
                  {t.description && <p className="text-[10px] text-gray-400 line-clamp-1 mb-2">{t.description}</p>}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t.category}</span>
                    <span className="text-[10px] text-gray-400">{t.node_count} elem.</span>
                    {t.page_count > 0 && <span className="text-[10px] text-violet-500">{t.page_count} pages</span>}
                  </div>
                </div>
                <div className="border-t border-gray-100 px-4 py-2 flex justify-between items-center">
                  <span className="text-[10px] text-gray-400">{t.author_name} — {t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : ""}</span>
                  {t.is_mine && <Bookmark size={12} className="text-indigo-400" />}
                </div>
              </div>
            ))}
            {userTemplates.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
                <BookmarkPlus size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="text-gray-400 text-sm mb-1">Aucun modele sauvegarde</p>
                <p className="text-[10px] text-gray-400">Ouvrez un schéma et cliquez "Sauver comme modele" pour le reutiliser</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="rounded-2xl border border-gray-200 shadow-2xl bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold text-xl flex items-center gap-2">
              <Crown size={20} className="text-amber-500" /> Choisissez votre offre
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Debloquez plus de fonctionnalités pour vos projets.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {/* Pro */}
            <div className="border-2 border-indigo-500 rounded-xl p-5 bg-indigo-50/30">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-base text-gray-900">Pro</h4>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">19</span>
                  <span className="text-sm text-gray-400">EUR/mois</span>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {["10 diagrammes", "Multi-pages & Architecture", "15 types + Web Blocs", "Templates & Pièces jointes"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-gray-600"><CheckCircle size={12} className="text-indigo-500 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade("pro")}
                disabled={checkoutLoading}
                className="w-full bg-indigo-600 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50"
                data-testid="upgrade-pro-btn"
              >
                {checkoutLoading ? "Redirection..." : "Passer Pro — 19 EUR/mois"}
              </button>
            </div>
            {/* Entreprise */}
            <div className="border border-gray-800 rounded-xl p-5 bg-gray-900 text-white">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-base flex items-center gap-1.5"><Crown size={14} className="text-amber-400" /> Entreprise</h4>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">49</span>
                  <span className="text-sm text-gray-400">EUR/mois</span>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {["Diagrammes illimités", "Tout le plan Pro", "Assistant IA vocal", "Export PDF & CdC", "Review client & Portail", "Voix off TTS & Video"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-gray-300"><CheckCircle size={12} className="text-amber-400 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade("enterprise")}
                disabled={checkoutLoading}
                className="w-full bg-white text-gray-900 py-2.5 rounded-lg text-sm font-bold hover:bg-gray-100 disabled:opacity-50"
                data-testid="upgrade-enterprise-btn"
              >
                {checkoutLoading ? "Redirection..." : "Passer Entreprise — 49 EUR/mois"}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center">
              Paiement sécurisé via Stripe. Annulable a tout moment.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) { setDeleteConfirmId(null); setDeleteConfirmTitle(""); } }}>
        <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-semibold text-base flex items-center gap-2">
              <Trash2 size={16} className="text-rose-500" /> Supprimer le diagramme ?
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Le diagramme <strong>"{deleteConfirmTitle}"</strong> sera deplace dans la corbeille. Vous pourrez le restaurer plus tard.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => { setDeleteConfirmId(null); setDeleteConfirmTitle(""); }}
              className="flex-1 px-4 py-2.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
              data-testid="cancel-delete-btn"
            >
              Annuler
            </button>
            <button
              onClick={confirmDelete}
              className="flex-1 bg-rose-600 text-white px-4 py-2.5 rounded-lg text-xs font-semibold hover:bg-rose-700 transition-colors flex items-center justify-center gap-1.5"
              data-testid="confirm-delete-btn"
            >
              <Trash2 size={13} /> Mettre a la corbeille
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
